import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) throw new Error(body?.message || 'EPC contract admin request failed');
  return body.data;
}

function contractParams(contractId?: string) {
  return contractId ? { params: { contractId } } : {};
}

export type EpcContractWorkspace = {
  contract: Record<string, unknown>;
  baseline: Record<string, unknown> | null;
  obligations: Record<string, unknown>[];
  securities: Record<string, unknown>[];
  insurance: Record<string, unknown>[];
  milestones: Record<string, unknown>[];
  calendar: Record<string, unknown>[];
  diagnostics: Record<string, unknown>[];
};

export type EpcCommercialForecast = {
  contractId: string;
  currency: string;
  contractValue: number;
  approvedVariations: number;
  expectedApprovedValue: number;
  certifiedToDate: number;
  certifiedGrossToDate?: number;
  appliedNotCertified: number;
  claimsOpen: number;
  retentionHeld: number;
  ldExposureAdvisory: number;
  projectedFinal: number;
  openingIncluded?: boolean;
  advanceRecovered?: number;
  asOf: string;
};

export type EpcContractControlTower = {
  contractId: string;
  forecast: EpcCommercialForecast;
  baseline: Record<string, unknown>;
  ldExposure: Record<string, unknown>;
  overdueNotices: Record<string, unknown>[];
  counts: {
    paymentApplications: number;
    certifications: number;
    variations: number;
    claims: number;
    eotRequests: number;
    finalAccounts: number;
    closeouts: number;
  };
  openVariations: Record<string, unknown>[];
  currency: string;
};

const base = '/epc/contract-admin';

export type EpcSovLine = {
  id?: string;
  item_code: string;
  description: string;
  unit?: string | null;
  quantity?: number | string | null;
  rate?: number | string | null;
  amount?: number | string | null;
  line_type?: string;
  wbs_code?: string | null;
  milestone_id?: string | null;
};

export type EpcContractMilestone = {
  id?: string;
  milestone_number: string;
  title: string;
  milestone_type?: string | null;
  planned_date?: string | null;
  due_date?: string | null;
  percent?: number | string | null;
  amount?: number | string | null;
  status?: string;
  actual_date?: string | null;
};

export type EpcIpcLineProgressEvidence = {
  seedKey: string;
  lineCode: string;
  wbsCode: string | null;
  matched: boolean;
  siteCumulativePct: number | null;
  previousCertifiedPct: number;
  suggestedThisPeriodPct: number | null;
  suggestedQty: number | null;
  basis: 'QUANTITY' | 'PACKAGE' | null;
  packages: Array<{ id: string; packageNumber: string; sitePct: number; lastLogDate: string | null }>;
  lastLogDate: string | null;
  milestone: { number: string; achieved: boolean | null; actualDate: string | null } | null;
};

export type EpcIpcProgressEvidence = {
  available: boolean;
  reason: 'cep_disabled' | 'cep_unavailable' | 'no_project' | 'error' | null;
  projectId: string | null;
  asOf: string | null;
  packageCount: number;
  matchedLines: number;
  unmatchedSovLines: number;
  note: string;
  lines: EpcIpcLineProgressEvidence[];
};

export type EpcAdvancePosition = {
  advancePct: number;
  entitlement: number;
  openingReceived: number;
  billed: number;
  available: number;
  vatPct: number;
  billable: boolean;
  requests: Array<{
    id: string;
    advance_number: string;
    amount: number | string;
    vat_amount: number | string;
    sales_invoice_id: string | null;
    invoice_number?: string | null;
    invoice_status?: string | null;
    created_at?: string;
  }>;
};

export type EpcOpeningPosition = {
  as_of_date: string | null;
  last_ipc_number: string | null;
  certified_to_date: number;
  retention_held: number;
  advance_received: number;
  advance_recovered: number;
  paid_to_date: number;
  sov_lines: { item_code: string; certified_quantity: number | null; certified_amount: number }[];
  notes: string | null;
};

export type EpcContractContent = {
  contractId: string;
  editable: boolean;
  milestonesAchievable?: boolean;
  sovLines: EpcSovLine[];
  milestones: EpcContractMilestone[];
  openingPosition: EpcOpeningPosition | null;
  summary: {
    contractValue: number;
    sovTotal: number;
    sovCount: number;
    milestoneCount: number;
    milestoneAmountTotal: number;
    milestonePercentTotal: number;
    issues: string[];
    advisories?: string[];
  };
};

export type EpcContentImportPreview = {
  rows: { row: number; status: 'valid' | 'error'; errors: string[]; data: Record<string, unknown> }[];
  validCount: number;
  errorCount: number;
  total: number;
  ready: boolean;
};

export type EpcContentKind = 'sov' | 'milestones';

async function downloadBlob(path: string): Promise<Blob> {
  const res = await api.getClient().get(path, { responseType: 'blob' });
  const data = res.data;
  if (data instanceof Blob) return data;
  return new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

function contentPath(contractId: string, kind: EpcContentKind) {
  return `${base}/contracts/${encodeURIComponent(contractId)}/${kind}`;
}

export const epcContractAdminService = {
  getRuntime: () => unwrap<Record<string, unknown>>(api.get(`${base}/runtime`)),

  getContractContent: (contractId: string) =>
    unwrap<EpcContractContent>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/content`)
    ),
  replaceSovLines: (
    contractId: string,
    lines: Record<string, unknown>[],
    opts?: { setContractValue?: boolean }
  ) =>
    unwrap<EpcContractContent>(
      api.put(contentPath(contractId, 'sov'), {
        lines,
        setContractValue: opts?.setContractValue === true,
      })
    ),
  replaceMilestones: (contractId: string, milestones: Record<string, unknown>[]) =>
    unwrap<EpcContractContent>(api.put(contentPath(contractId, 'milestones'), { milestones })),
  getAdvancePosition: (contractId: string) =>
    unwrap<EpcAdvancePosition>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/advance-payments`)
    ),
  requestAdvancePayment: (
    contractId: string,
    payload: { amount?: number; notes?: string; departmentId?: string | null }
  ) =>
    unwrap<{ advanceNumber: string; salesInvoiceId: string; invoiceNumber: string; amount: number; vatAmount: number }>(
      api.post(`${base}/contracts/${encodeURIComponent(contractId)}/advance-payments`, payload)
    ),
  setMilestoneAchievement: (
    contractId: string,
    milestoneId: string,
    payload: { achieved: boolean; actualDate?: string | null }
  ) =>
    unwrap<EpcContractContent>(
      api.post(
        `${contentPath(contractId, 'milestones')}/${encodeURIComponent(milestoneId)}/achievement`,
        payload
      )
    ),
  setOpeningPosition: (contractId: string, payload: Record<string, unknown> | null) =>
    unwrap<EpcContractContent>(
      api.put(
        `${base}/contracts/${encodeURIComponent(contractId)}/opening-position`,
        payload ?? { clear: true }
      )
    ),
  downloadContentTemplate: (kind: EpcContentKind) => downloadBlob(`${base}/${kind}/template`),
  exportContent: (contractId: string, kind: EpcContentKind) =>
    downloadBlob(`${contentPath(contractId, kind)}/export`),
  previewContentImport: (contractId: string, kind: EpcContentKind, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return unwrap<EpcContentImportPreview>(
      api.post(`${contentPath(contractId, kind)}/import/preview`, fd)
    );
  },
  commitContentImport: (
    contractId: string,
    kind: EpcContentKind,
    file: File,
    opts?: { setContractValue?: boolean }
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.setContractValue) fd.append('setContractValue', 'true');
    return unwrap<EpcContractContent>(
      api.post(`${contentPath(contractId, kind)}/import/commit`, fd)
    );
  },

  getWorkspace: (contractId: string) =>
    unwrap<EpcContractWorkspace>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/workspace`)
    ),

  getParticulars: (contractId: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/particulars`)
    ),
  patchParticulars: (contractId: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/contracts/${encodeURIComponent(contractId)}/particulars`, payload)
    ),

  listObligations: (contractId: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/obligations`)
    ),
  createObligation: (contractId: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/contracts/${encodeURIComponent(contractId)}/obligations`, payload)
    ),
  patchObligation: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/obligations/${encodeURIComponent(id)}`, payload)
    ),

  listSecurities: (contractId: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/securities`)
    ),
  createSecurity: (contractId: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/contracts/${encodeURIComponent(contractId)}/securities`, payload)
    ),
  patchSecurity: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/securities/${encodeURIComponent(id)}`, payload)
    ),

  listInsurance: (contractId: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/insurance`)
    ),
  createInsurance: (contractId: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/contracts/${encodeURIComponent(contractId)}/insurance`, payload)
    ),
  patchInsurance: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/insurance/${encodeURIComponent(id)}`, payload)
    ),

  getCalendar: (contractId: string) =>
    unwrap<{ events: Record<string, unknown>[] }>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/calendar`)
    ),
  getDiagnostics: (contractId: string) =>
    unwrap<{ findings: Record<string, unknown>[] }>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/diagnostics`)
    ),

  listNotices: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/notices`, contractParams(contractId))),
  listOverdueNotices: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/notices/overdue`, contractParams(contractId))
    ),
  getNotice: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/notices/${encodeURIComponent(id)}`)),
  createNotice: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/notices`, payload)),
  patchNotice: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/notices/${encodeURIComponent(id)}`, payload)
    ),
  issueNotice: (id: string) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/notices/${encodeURIComponent(id)}/issue`)),

  listPaymentApplications: (contractId?: string, filters?: { status?: string }) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/payment-applications`, {
        params: { ...(contractId ? { contractId } : {}), ...(filters?.status ? { status: filters.status } : {}) },
      })
    ),
  getPaymentApplicationDraft: (contractId: string, excludeApplicationId?: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/payment-application-draft`, {
        params: excludeApplicationId ? { excludeApplicationId } : undefined,
      })
    ),
  getIpcProgressEvidence: (
    contractId: string,
    opts: { periodTo?: string | null; excludeApplicationId?: string } = {}
  ) =>
    unwrap<EpcIpcProgressEvidence>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/ipc-progress-evidence`, {
        params: {
          ...(opts.periodTo ? { periodTo: opts.periodTo } : {}),
          ...(opts.excludeApplicationId ? { excludeApplicationId: opts.excludeApplicationId } : {}),
        },
      })
    ),
  getPaymentApplication: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/payment-applications/${encodeURIComponent(id)}`)
    ),
  createPaymentApplication: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/payment-applications`, payload)),
  patchPaymentApplication: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/payment-applications/${encodeURIComponent(id)}`, payload)
    ),
  submitPaymentApplication: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/payment-applications/${encodeURIComponent(id)}/submit`)
    ),

  listCertifications: (contractId?: string, filters?: { status?: string }) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/certifications`, {
        params: { ...(contractId ? { contractId } : {}), ...(filters?.status ? { status: filters.status } : {}) },
      })
    ),
  getCertification: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/certifications/${encodeURIComponent(id)}`)
    ),
  createCertification: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/certifications`, payload)),
  confirmCertification: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/certifications/${encodeURIComponent(id)}/confirm`)
    ),
  convertCertificationToSalesInvoice: (
    id: string,
    payload?: { departmentId?: string | null }
  ) =>
    unwrap<Record<string, unknown>>(
      api.post(
        `${base}/certifications/${encodeURIComponent(id)}/convert-to-sales-invoice`,
        payload || {}
      )
    ),

  listChangeEvents: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/change-events`, contractParams(contractId))
    ),
  createChangeEvent: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/change-events`, payload)),
  patchChangeEvent: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/change-events/${encodeURIComponent(id)}`, payload)
    ),

  listVariations: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/variations`, contractParams(contractId))),
  getVariation: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/variations/${encodeURIComponent(id)}`)),
  createVariation: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/variations`, payload)),
  patchVariation: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/variations/${encodeURIComponent(id)}`, payload)
    ),
  submitVariation: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/variations/${encodeURIComponent(id)}/submit`)
    ),
  reconcileBaseline: (contractId: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/baseline-reconcile`)
    ),

  listClaims: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/claims`, contractParams(contractId))),
  getClaim: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/claims/${encodeURIComponent(id)}`)),
  createClaim: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/claims`, payload)),
  patchClaim: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.patch(`${base}/claims/${encodeURIComponent(id)}`, payload)),
  submitClaim: (id: string) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/claims/${encodeURIComponent(id)}/submit`)),

  listEotRequests: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/eot-requests`, contractParams(contractId))
    ),
  getEotRequest: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/eot-requests/${encodeURIComponent(id)}`)),
  createEotRequest: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/eot-requests`, payload)),
  patchEotRequest: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/eot-requests/${encodeURIComponent(id)}`, payload)
    ),
  submitEotRequest: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/eot-requests/${encodeURIComponent(id)}/submit`)
    ),
  getLdExposure: (contractId: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/ld-exposure`)
    ),

  getForecast: (contractId: string) =>
    unwrap<EpcCommercialForecast>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/forecast`)
    ),
  createForecastSnapshot: (payload: { contractId: string }) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/forecast-snapshots`, payload)),
  getControlTower: (contractId: string) =>
    unwrap<EpcContractControlTower>(
      api.get(`${base}/contracts/${encodeURIComponent(contractId)}/control-tower`)
    ),

  listFinalAccounts: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(
      api.get(`${base}/final-accounts`, contractParams(contractId))
    ),
  getFinalAccount: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.get(`${base}/final-accounts/${encodeURIComponent(id)}`)
    ),
  createFinalAccount: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/final-accounts`, payload)),
  submitFinalAccount: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/final-accounts/${encodeURIComponent(id)}/submit`)
    ),

  listCloseouts: (contractId?: string) =>
    unwrap<Record<string, unknown>[]>(api.get(`${base}/closeouts`, contractParams(contractId))),
  getCloseout: (id: string) =>
    unwrap<Record<string, unknown>>(api.get(`${base}/closeouts/${encodeURIComponent(id)}`)),
  createCloseout: (payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(api.post(`${base}/closeouts`, payload)),
  patchCloseout: (id: string, payload: Record<string, unknown>) =>
    unwrap<Record<string, unknown>>(
      api.patch(`${base}/closeouts/${encodeURIComponent(id)}`, payload)
    ),
  submitCloseout: (id: string) =>
    unwrap<Record<string, unknown>>(
      api.post(`${base}/closeouts/${encodeURIComponent(id)}/submit`)
    ),
};
